/**
 * @file: useCurrentUserRole.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useAuthContext } from '@euler/app/flows/auth/context';
import { useCachedQuery } from '@euler/functions/useCachedQuery';
import { useServiceFactory } from '@euler/services/factory';
import { useBehaviorSubject } from '@euler/utils/hooks';
import produce from 'immer';

export const useCurrentUserRole = () => {
  const { authState$ } = useAuthContext();
  const { userService } = useServiceFactory();

  const [authState, setAuthState] = useBehaviorSubject(authState$);

  const isAuthenticated = authState.isAuthenticated;
  const userId = isAuthenticated
    ? authState.authenticatedUserInfo.user.userId
    : undefined;

  const { state: role } = useCachedQuery<string | undefined>(
    'current-user-role.json',
    __DEV__ ? 1 : 10,
    async () => {
      if (!isAuthenticated) return undefined;
      return await userService
        .getCurrentUserRole()
        .then(res => {
          setAuthState(state =>
            produce(state, draft => {
              if (!draft.isAuthenticated || !state.isAuthenticated) {
                return;
              }

              if (state.authenticatedUserInfo.user.role !== res) {
                draft.authenticatedUserInfo.user.role = res;
              }
            }),
          );
          return res;
        })
        .catch(err => {
          console.error(err);
          return undefined;
        });
    },
    isAuthenticated,
    {
      initialState: isAuthenticated
        ? authState.authenticatedUserInfo.user.role
        : undefined,
      validate: () => false,
      deps: [userId],
    },
  );

  if (!isAuthenticated) return undefined;

  return role;
};
